import { useEffect } from "react";
import type { ViewContext } from "../types";
import { BookingApiError } from "../api";
import { BookingLoading, BookingProblem, Notice, RecordNotFound } from "../parts";
import { isOtherSchool } from "../school";
import { useClassroom } from "./manage/useClassroom";
import { deriveFlexitimeConfirmationState } from "./flexitime/deriveFlexitimeConfirmationState";
import { confirmCopy, hasConfirmation, redirectForVerb } from "./flexitime/flexitime";

// /bookings/<id>/<verb>: the flexitime confirmation email link. Port of
// v1's (school)/(chrome)/bookings/[id]/[hash] page: it works out the
// booking's state from the verb and says what it is (see `confirmCopy` for
// why it changes nothing). The booking is read from the manage page's
// endpoint, which carries the same fields v1 read (confirmation,
// anytimeClass, the session's location).

export function FlexitimeConfirmView({ ctx, bookingId, verb }: { ctx: ViewContext; bookingId: string; verb: string }) {
  const redirect = redirectForVerb(verb, bookingId, ctx.links);
  useEffect(() => {
    if (redirect) window.location.replace(redirect);
  }, [redirect]);
  if (redirect) return <BookingLoading rows={1} />;
  return <Confirmation ctx={ctx} bookingId={bookingId} verb={verb} />;
}

function Confirmation({ ctx, bookingId, verb }: { ctx: ViewContext; bookingId: string; verb: string }) {
  const classroom = useClassroom(ctx.api, bookingId);
  if (classroom.isPending) return <BookingLoading rows={1} />;
  if (classroom.isError) {
    return classroom.error instanceof BookingApiError && classroom.error.status === 404 ? (
      <RecordNotFound />
    ) : (
      <BookingProblem title="This booking couldn’t be loaded" message="Please try again in a moment." onRetry={() => void classroom.refetch()} />
    );
  }

  const { booking, session, school, course } = classroom.data;
  if (isOtherSchool(ctx.businessId, school, course?.schoolId, booking?.school)) return <RecordNotFound />;
  const copy = confirmCopy(
    deriveFlexitimeConfirmationState({
      isAlreadyConfirmed: hasConfirmation(booking.confirmation),
      isAnytimeClass: Boolean(booking.anytimeClass),
      isPhysicalLocation: session?.locationId !== "-1",
      confirmationAction: verb,
    }),
  );

  return (
    <div className="mx-auto max-w-lg space-y-4 py-8">
      <h1 className="text-2xl font-semibold">{copy.title}</h1>
      <Notice tone={copy.tone}>{copy.message}</Notice>
      {copy.manageLink && (
        <a className="inline-block text-sm font-medium text-primary underline-offset-4 hover:underline" href={ctx.links.manage(bookingId)}>
          View the booking
        </a>
      )}
    </div>
  );
}
